import React, { useEffect } from "react";
import { useSelector } from "react-redux";

import {
	Box,
	HStack,
	Heading,
	Text,
	VStack,
	useColorModeValue,
	List,
	ListItem,
	ListIcon,
	Button,
	SimpleGrid,
	Card,
	CardBody,
	CardHeader,
	Container,
	Badge,
	Icon,
} from "@chakra-ui/react";
import moment from "moment/moment";
import { FaCheckCircle, FaBuilding, FaUser, FaBriefcase } from "react-icons/fa";

import { API } from "../../utils/http";
import { fullDateFormat } from "../../utils/types";

function PriceWrapper({ children, isPopular }) {
	const bgColor = useColorModeValue("white", "gray.800");
	const borderColor = useColorModeValue("gray.200", "gray.600");

	return (
		<Box
			position="relative"
			transition="transform 0.3s ease"
			_hover={{ transform: "translateY(-8px)" }}
			mb={4}
			shadow="xl"
			bg={bgColor}
			borderWidth="1px"
			borderColor={isPopular ? "red.400" : borderColor}
			borderRadius="2xl"
			overflow="hidden"
		>
			{children}
		</Box>
	);
}

export default function Subscription() {
	const subscriptionPlan = useSelector((state) => state.user.user.subscriptionPlan);
	const customerId = useSelector((state) => state.user.user.stripeCustomerId);

	useEffect(() => {
		API.get("/get-plans").then((r) => {
			console.log(r.data);
		});
	}, []);

	const manageSubscription = () => {
		API.post("/create-portal-session", { customerId }).then((r) => {
			window.open(r.data.portalUrl, "_self");
		});
	};

	const subscribe = (plan) => {
		console.log(plan);
		API.post("/create-checkout-session", {
			priceId: "price_1O4qxWFi3qZeBet7f3MsYIle",
			customerId,
		}).then((r) => {
			window.open(r.data.sessionUrl, "_self");
		});
	};

	const bgColor = useColorModeValue("gray.50", "gray.700");

	return (
		<Container maxW="7xl" py={12}>
			{subscriptionPlan && (
				<Card mb={12} variant="outline" borderRadius="xl">
					<CardHeader bg={bgColor} borderBottomWidth="1px">
						<HStack spacing={3}>
							<Icon as={FaBuilding} w={6} h={6} color="red.400" />
							<Text fontWeight="bold" fontSize="2xl">
								Current Subscription
							</Text>
						</HStack>
					</CardHeader>
					<CardBody>
						<SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
							<Box>
								<Text color="gray.500" mb={1}>
									Plan
								</Text>
								<Text fontSize="xl" fontWeight="semibold">
									{subscriptionPlan.planName}
								</Text>
							</Box>
							<Box>
								<Text color="gray.500" mb={1}>
									Price
								</Text>
								<Text fontSize="xl" fontWeight="semibold">
									{subscriptionPlan.planPrice / 100} {subscriptionPlan.planCurrency}
									<Text as="span" fontSize="md" color="gray.500">
										/month
									</Text>
								</Text>
							</Box>
							<Box>
								<Text color="gray.500" mb={1}>
									Period Start
								</Text>
								<Text fontSize="lg">{moment.unix(subscriptionPlan.currentPeriodStart).format(fullDateFormat)}</Text>
							</Box>
							<Box>
								<Text color="gray.500" mb={1}>
									Period End
								</Text>
								<Text fontSize="lg">{moment.unix(subscriptionPlan.currentPeriodEnd).format(fullDateFormat)}</Text>
							</Box>
						</SimpleGrid>
						<Button mt={6} size="lg" colorScheme="red" onClick={manageSubscription}>
							Manage Subscription
						</Button>
					</CardBody>
				</Card>
			)}

			<VStack spacing={4} textAlign="center" mb={12}>
				<Heading as="h1" fontSize={{ base: "3xl", md: "4xl" }} bgGradient="linear(to-r, red.400, red.600)" bgClip="text">
					Choose Your Perfect Plan
				</Heading>
				<Text fontSize="lg" color="gray.500" maxW="2xl">
					Select a plan that best fits your needs. All plans include our core features with different levels of access and support.
				</Text>
			</VStack>

			<SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 6, lg: 10 }}>
				<PriceWrapper>
					<Box py={8} px={8}>
						<HStack spacing={3} mb={4}>
							<Icon as={FaUser} w={6} h={6} color="red.400" />
							<Text fontSize="2xl" fontWeight="bold">
								Basic
							</Text>
						</HStack>
						<HStack mb={6}>
							<Text fontSize="5xl" fontWeight="900">
								250
							</Text>
							<Box>
								<Text fontSize="2xl" fontWeight="600" lineHeight="1">
									RON
								</Text>
								<Text fontSize="sm" color="gray.500">
									/month
								</Text>
							</Box>
						</HStack>
						<List spacing={4}>
							<ListItem>
								<HStack spacing={2} align="flex-start">
									<ListIcon as={FaCheckCircle} color="green.500" mt={1} />
									<Text>Up to 10 properties</Text>
								</HStack>
							</ListItem>
							<ListItem>
								<HStack spacing={2} align="flex-start">
									<ListIcon as={FaCheckCircle} color="green.500" mt={1} />
									<Text>Basic reporting tools</Text>
								</HStack>
							</ListItem>
							<ListItem>
								<HStack spacing={2} align="flex-start">
									<ListIcon as={FaCheckCircle} color="green.500" mt={1} />
									<Text>Email support</Text>
								</HStack>
							</ListItem>
						</List>
						<Button mt={8} w="full" size="lg" colorScheme="red" variant={subscriptionPlan ? "outline" : "solid"} onClick={() => subscribe("basic")}>
							{subscriptionPlan ? "Switch Plan" : "Get Started"}
						</Button>
					</Box>
				</PriceWrapper>

				<PriceWrapper isPopular>
					<Box position="relative">
						<Badge position="absolute" top={4} right={4} colorScheme="red" variant="solid" rounded="full" px={3} py={1}>
							Popular
						</Badge>
						<Box py={8} px={8}>
							<HStack spacing={3} mb={4}>
								<Icon as={FaBriefcase} w={6} h={6} color="red.400" />
								<Text fontSize="2xl" fontWeight="bold">
									Business
								</Text>
							</HStack>
							<HStack mb={6}>
								<Text fontSize="5xl" fontWeight="900">
									149
								</Text>
								<Box>
									<Text fontSize="2xl" fontWeight="600" lineHeight="1">
										USD
									</Text>
									<Text fontSize="sm" color="gray.500">
										/month
									</Text>
								</Box>
							</HStack>
							<List spacing={4}>
								<ListItem>
									<HStack spacing={2} align="flex-start">
										<ListIcon as={FaCheckCircle} color="green.500" mt={1} />
										<Text>Up to 50 properties</Text>
									</HStack>
								</ListItem>
								<ListItem>
									<HStack spacing={2} align="flex-start">
										<ListIcon as={FaCheckCircle} color="green.500" mt={1} />
										<Text>Advanced analytics</Text>
									</HStack>
								</ListItem>
								<ListItem>
									<HStack spacing={2} align="flex-start">
										<ListIcon as={FaCheckCircle} color="green.500" mt={1} />
										<Text>Priority support</Text>
									</HStack>
								</ListItem>
								<ListItem>
									<HStack spacing={2} align="flex-start">
										<ListIcon as={FaCheckCircle} color="green.500" mt={1} />
										<Text>Custom reporting</Text>
									</HStack>
								</ListItem>
							</List>
							<Button mt={8} w="full" size="lg" colorScheme="red" variant={subscriptionPlan ? "outline" : "solid"} onClick={() => subscribe("business")}>
								{subscriptionPlan ? "Switch Plan" : "Get Started"}
							</Button>
						</Box>
					</Box>
				</PriceWrapper>

				<PriceWrapper>
					<Box py={8} px={8}>
						<HStack spacing={3} mb={4}>
							<Icon as={FaBuilding} w={6} h={6} color="red.400" />
							<Text fontSize="2xl" fontWeight="bold">
								Enterprise
							</Text>
						</HStack>
						<HStack mb={6}>
							<Text fontSize="5xl" fontWeight="900">
								349
							</Text>
							<Box>
								<Text fontSize="2xl" fontWeight="600" lineHeight="1">
									USD
								</Text>
								<Text fontSize="sm" color="gray.500">
									/month
								</Text>
							</Box>
						</HStack>
						<List spacing={4}>
							<ListItem>
								<HStack spacing={2} align="flex-start">
									<ListIcon as={FaCheckCircle} color="green.500" mt={1} />
									<Text>Unlimited properties</Text>
								</HStack>
							</ListItem>
							<ListItem>
								<HStack spacing={2} align="flex-start">
									<ListIcon as={FaCheckCircle} color="green.500" mt={1} />
									<Text>Full API access</Text>
								</HStack>
							</ListItem>
							<ListItem>
								<HStack spacing={2} align="flex-start">
									<ListIcon as={FaCheckCircle} color="green.500" mt={1} />
									<Text>24/7 dedicated support</Text>
								</HStack>
							</ListItem>
						</List>
						<Button mt={8} w="full" size="lg" colorScheme="red" variant={subscriptionPlan ? "outline" : "solid"} onClick={() => subscribe("enterprise")}>
							{subscriptionPlan ? "Switch Plan" : "Get Started"}
						</Button>
					</Box>
				</PriceWrapper>
			</SimpleGrid>
		</Container>
	);
}
